import React, { useState, useCallback } from 'react';
import { 
  Plane, Users, Armchair, UtensilsCrossed, Briefcase, 
  Eye, Download, Edit2, X, Check,
  User, Luggage, Plus, QrCode, ChevronDown, ChevronUp
} from 'lucide-react';
import { updateBusiness4FlyoTraveller } from '../../services/api';

// Color classes - defined outside component
const colorClasses = {
  blue: 'bg-blue-50 text-blue-600',
  purple: 'bg-purple-50 text-purple-600',
  green: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
};

/**
 * Services Tab Component for Business4Flyo
 * Shows flight-specific services: seat, meal, baggage, boarding pass
 * Card-based design matching CheckInDetailsPage TravellerCard
 * @param {Object} props - Component props
 * @param {Array} props.flights - Array of flight objects with travellers
 * @param {string|number} props.ticketId - Ticket ID
 * @param {number} props.clientId - Client ID
 * @param {number} props.agentId - Agent ID
 * @param {Function} props.onUpdate - Callback to refresh data after update
 * @returns {JSX.Element} B4FServicesTab component
 */
const B4FServicesTab = ({ flights, ticketId, clientId, agentId, onUpdate }) => {
  const [selectedFlightIndex, setSelectedFlightIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  
  // Inline edit state
  const [inlineEditingField, setInlineEditingField] = useState(null);
  const [inlineEditValue, setInlineEditValue] = useState('');
  const [editingTraveller, setEditingTraveller] = useState(null);

  // Get selected flight
  const selectedFlight = flights && flights.length > 0 ? flights[selectedFlightIndex] : null;
  const travellers = selectedFlight?.travellers || [];

  // Format sector string
  const formatSector = (departure, arrival) => {
    const dep = departure?.iata || 'N/A';
    const arr = arrival?.iata || 'N/A';
    return `${dep} → ${arr}`;
  };

  // Start inline editing
  const startInlineEdit = useCallback((traveller, field, value) => {
    setEditingTraveller(traveller);
    setInlineEditingField(field);
    setInlineEditValue(value || '');
  }, []);

  // Cancel inline editing
  const cancelInlineEdit = useCallback(() => {
    setInlineEditingField(null);
    setInlineEditValue('');
    setEditingTraveller(null);
  }, []);

  // Save inline edit
  const saveInlineEdit = useCallback(async () => {
    if (!editingTraveller || !inlineEditingField || !selectedFlight) return;
    if (!agentId || !ticketId || !clientId) {
      alert('Missing required information');
      return;
    }

    try {
      setLoading(true);

      // Build flightData array with the updated field
      const flightDataPayload = [{
        flightId: selectedFlight.flightId,
        seat: inlineEditingField === 'seat' ? inlineEditValue : (editingTraveller.seat || ''),
        meal: inlineEditingField === 'meal' ? inlineEditValue : (editingTraveller.meal || ''),
        baggage: {
          cabin: inlineEditingField === 'baggageCabin' ? inlineEditValue : (editingTraveller.baggage?.cabin || ''),
          checkin: inlineEditingField === 'baggageCheckin' ? inlineEditValue : (editingTraveller.baggage?.checkin || ''),
          additional: inlineEditingField === 'baggageAdditional' ? inlineEditValue : (editingTraveller.baggage?.additional || '')
        }
      }];

      // Build the payload
      const payload = {
        agentId: parseInt(agentId),
        travellerId: editingTraveller.travellerId,
        ticketId: parseInt(ticketId),
        clientId: parseInt(clientId),
        firstName: editingTraveller.firstname || '',
        lastName: editingTraveller.lastname || '',
        flightData: flightDataPayload
      };

      await updateBusiness4FlyoTraveller(payload);
      
      setInlineEditingField(null);
      setInlineEditValue('');
      setEditingTraveller(null);
      
      if (onUpdate) onUpdate();
    } catch (error) {
      alert(`Error updating service: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [editingTraveller, inlineEditingField, inlineEditValue, agentId, ticketId, clientId, selectedFlight, onUpdate]);

  if (!flights || flights.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
          <Plane className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-sm font-medium text-gray-900 mb-1">No Flight Information</h3>
        <p className="text-xs text-gray-500">No flight details available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Commented out as per request */}
      {/*
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/25">
          <Briefcase className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Services & Passengers</h2>
          <p className="text-xs text-gray-500">Flight-specific services and boarding passes</p>
        </div>
      </div>
      */}

      {/* Flight Selector Tabs - Reduced size */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {flights.map((flight, index) => (
          <button
            key={flight.flightId || index}
            onClick={() => {
              setSelectedFlightIndex(index);
              cancelInlineEdit();
            }}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left min-w-[140px] transition-all ${
              selectedFlightIndex === index
                ? 'bg-blue-600 text-white shadow-md shadow-blue-500/20'
                : 'bg-white border border-gray-100 text-gray-700 hover:border-blue-200 hover:shadow-sm'
            }`}
          >
            <Plane className="w-3.5 h-3.5 flex-shrink-0" />
            <div>
              <p className="text-xs font-semibold">
                Flight {index + 1}: {formatSector(flight.departure, flight.arrival)}
              </p>
              <p className={`text-[10px] ${selectedFlightIndex === index ? 'text-blue-100' : 'text-gray-500'}`}>
                {flight.flightNumber || 'N/A'}
              </p>
            </div>
          </button>
        ))}
      </div>

      {/* Services for Selected Flight */}
      {selectedFlight && (
        <div className="space-y-6">
          {/* Flight Info Header - Commented out as per request */}
          {/*
          <div className="flex items-center gap-4 bg-white rounded-xl border border-gray-100 shadow-sm px-6 py-4">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Plane className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedFlight.flightNumber || 'N/A'}
              </h3>
              <p className="text-sm text-gray-500">
                {formatSector(selectedFlight.departure, selectedFlight.arrival)}
                {selectedFlight.departure?.date && ` • ${selectedFlight.departure.date}`}
              </p>
            </div>
            <div className="ml-auto text-right">
              <span className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-600 text-xs font-semibold rounded-full border border-blue-100">
                {travellers.length} Passenger{travellers.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          */}

          {/* Traveller Cards Grid */}
          {travellers.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">No Passengers</h3>
              <p className="text-xs text-gray-500">No passengers for this flight</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {travellers.map((traveller, index) => (
                <TravellerServicesCard
                  key={traveller.travellerId || index}
                  traveller={traveller}
                  index={index}
                  loading={loading}
                  inlineEditingField={inlineEditingField}
                  editingTraveller={editingTraveller}
                  inlineEditValue={inlineEditValue}
                  setInlineEditValue={setInlineEditValue}
                  startInlineEdit={startInlineEdit}
                  saveInlineEdit={saveInlineEdit}
                  cancelInlineEdit={cancelInlineEdit}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Traveller Services Card Component - Card-Based Design
const TravellerServicesCard = React.memo(({
  traveller,
  index,
  loading,
  inlineEditingField,
  editingTraveller,
  inlineEditValue,
  setInlineEditValue,
  startInlineEdit,
  saveInlineEdit,
  cancelInlineEdit
}) => {
  const isEditing = editingTraveller?.travellerId === traveller.travellerId;
  const hasBoardingPass = !!traveller.boardingPassUrl;
  
  // State for collapsible services section - collapsed by default
  const [isServicesExpanded, setIsServicesExpanded] = useState(false);

  // Service row component
  const ServiceRow = ({ icon: Icon, label, value, field, color = 'blue' }) => {
    const isFieldEditing = isEditing && inlineEditingField === field;
    const displayValue = value || 'Not assigned';

    return (
      <div className="flex items-center justify-between py-2.5 group hover:bg-gray-50 rounded-lg px-2 -mx-2 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
            {isFieldEditing ? (
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="text"
                  value={inlineEditValue}
                  onChange={(e) => setInlineEditValue(e.target.value)}
                  className="text-sm border border-blue-300 rounded-md px-2 py-1 w-32 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveInlineEdit();
                    if (e.key === 'Escape') cancelInlineEdit();
                  }}
                />
                <button
                  onClick={saveInlineEdit}
                  disabled={loading}
                  className="p-1 rounded-md bg-green-100 text-green-600 hover:bg-green-200"
                  title="Save"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={cancelInlineEdit}
                  disabled={loading}
                  className="p-1 rounded-md bg-gray-100 text-gray-600 hover:bg-gray-200"
                  title="Cancel"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <p className={`text-sm font-medium ${value ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                {displayValue}
              </p>
            )}
          </div>
        </div>
        {!isFieldEditing && (
          <button
            onClick={() => startInlineEdit(traveller, field, value)}
            disabled={loading}
            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
            title={`Edit ${label}`}
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
      {/* Header Section */}
      <div className="px-6 py-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                {traveller.firstname || 'N/A'} {traveller.lastname || 'N/A'}
              </h3>
              <p className="text-sm text-gray-500">
                Passenger {index + 1}
                {traveller.seat && ` · Seat ${traveller.seat}`}
              </p>
            </div>
          </div>
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${
            hasBoardingPass 
              ? 'bg-green-100 text-green-700 border-green-200' 
              : 'bg-amber-100 text-amber-700 border-amber-200'
          }`}>
            {hasBoardingPass ? 'Available' : 'Not Available'}
          </span>
        </div>
      </div>

      {/* QR Code Section */}
      <div className="px-6 py-6 flex justify-center bg-gray-50/50">
        {hasBoardingPass ? (
          <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm">
            <img
              src={traveller.boardingPassUrl}
              alt="Boarding Pass QR"
              className="h-40 w-40 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTYwIiBoZWlnaHQ9IjE2MCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNHB4IiBmaWxsPSIjOWNhM2FmIj5RUiBDb2RlPC90ZXh0Pjwvc3ZnPg==";
              }}
            />
          </div>
        ) : (
          <div className="p-4 bg-white border border-gray-200 rounded-xl shadow-sm flex flex-col items-center justify-center h-40 w-40">
            <QrCode className="w-12 h-12 text-gray-300 mb-2" />
            <p className="text-xs text-gray-400 text-center">No boarding pass<br/>available</p>
          </div>
        )}
      </div>

      {/* Services Section - Collapsible */}
      <div className="px-6 py-4 border-t border-gray-100">
        <button
          onClick={() => setIsServicesExpanded(!isServicesExpanded)}
          className="flex items-center justify-between w-full group"
        >
          <div className="flex items-center gap-2">
            <Plane className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-semibold text-gray-900">Traveller Services</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">
              {isServicesExpanded ? 'Hide' : 'Show'}
            </span>
            <div className="p-1.5 rounded-lg bg-gray-100 text-gray-500 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
              {isServicesExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </div>
          </div>
        </button>
        
        {isServicesExpanded && (
          <div className="space-y-1 mt-3 pt-3 border-t border-gray-100">
            <ServiceRow 
              icon={Armchair} 
              label="Seat" 
              value={traveller.seat}
              field="seat"
              color="blue"
            />
            <ServiceRow 
              icon={UtensilsCrossed} 
              label="Meal" 
              value={traveller.meal}
              field="meal"
              color="purple"
            />
            <ServiceRow 
              icon={Briefcase} 
              label="Cabin Baggage" 
              value={traveller.baggage?.cabin}
              field="baggageCabin"
              color="green"
            />
            <ServiceRow 
              icon={Luggage} 
              label="Check-in Baggage" 
              value={traveller.baggage?.checkin}
              field="baggageCheckin"
              color="amber"
            />
            <ServiceRow 
              icon={Plus} 
              label="Additional Baggage" 
              value={traveller.baggage?.additional}
              field="baggageAdditional"
              color="purple"
            />
          </div>
        )}
      </div>

      {/* Actions Section */}
      {hasBoardingPass && (
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/30">
          <div className="flex gap-3">
            <a
              href={traveller.boardingPassUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-white text-gray-700 border border-gray-200 hover:bg-gray-50 transition-all"
            >
              <Eye className="w-4 h-4" />
              <span>View Boarding Pass</span>
            </a>
            <a
              href={traveller.boardingPassUrl}
              download
              className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm bg-blue-600 text-white hover:bg-blue-700 transition-all shadow-md shadow-blue-500/25"
            >
              <Download className="w-4 h-4" />
              <span>Download</span>
            </a>
          </div>
        </div>
      )}
    </div>
  );
});

TravellerServicesCard.displayName = 'TravellerServicesCard';

export default B4FServicesTab;
